/**
 * The profile-identity contract, shared by every layer so the enforcement points
 * can never drift apart: the backend domain rule
 * (`features/profile/domain/profile-identity.ts`), the ingestion paths that
 * normalise scraped URLs, and the frontend's client-side validation
 * (`src/features/profile/edit-profile-identity/domain/profile-identity-draft.validation.ts`).
 *
 * A drift is not cosmetic here: a frontend that accepts a URL shape the backend
 * rejects produces a form that submits and gets a 400 back; and a frontend that
 * normalises differently from ingestion would let two spellings of the same
 * LinkedIn profile coexist, which the `linkedin_url` UNIQUE index would never
 * catch because they are, textually, two different URLs.
 */

/**
 * The one way a full name is built from its parts. The collected `full_name` is
 * whatever the scraper reported, but an admin-edited one is always recomposed,
 * hence the collapse of inner whitespace, so a cleared first name cannot leave a
 * double space behind.
 */
export function composeFullName(firstName: string, lastName: string): string {
    return `${firstName} ${lastName}`.trim().replace(/\s+/g, " ");
}

/**
 * Strip query params, hash and locale subdomains from a LinkedIn URL, and drop
 * the trailing slash. Converts e.g.
 *   https://fr.linkedin.com/in/john-doe/?miniProfileUrn=urn%3A…
 * → https://www.linkedin.com/in/john-doe
 *
 * A member profile keeps `/in/<slug>` and nothing after it: LinkedIn appends a
 * language (`/in/john-doe/en/`) or a section (`/in/john-doe/details/experience/`)
 * that a client file carries over, and that tail made the stored profile
 * unfindable: the import created a duplicate named after the slug (#654).
 *
 * Anything that is not a linkedin.com URL, or that does not parse, is returned
 * untouched; validation is a separate concern and rejects it downstream.
 */
export function normalizeLinkedInUrl(url: string): string {
    try {
        const u = new URL(url.trim());
        // Only normalise linkedin.com URLs
        if (!u.hostname.endsWith("linkedin.com")) return url;
        // Strip query params, hash, and locale subdomains (fr.linkedin.com, en.linkedin.com, etc.)
        // Always use canonical https://www.linkedin.com
        const pathname = u.pathname.replace(/\/+$/, ""); // strip trailing slashes
        const member = /^\/in\/[^/]+/.exec(pathname);
        return `https://www.linkedin.com${member ? member[0] : pathname}`;
    } catch {
        return url; // unparseable → return as-is, validation will reject it
    }
}

/** Whether the URL points at a member profile (`/in/…`) rather than a company or school page. */
export function isValidLinkedInProfileUrl(url: string): boolean {
    return /^https?:\/\/(www\.)?linkedin\.com\/in\//.test(url);
}
